// 내 프로필 페이지
// - 좌: 기본 정보·근무/소속·비밀번호 / 우: 소셜·명함·구독·탈퇴
// - 근무 상태(/manager-status), 소속 변경 권한 가드
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { Roles, RoleLabels } from '../auth/rbac'
import { api, resolveApiAssetUrl } from '../lib/api'
import { formatApiDateTimeDisplay } from '../lib/date'
import { resizeForProfileUpload } from '../lib/imageResize'
import InlineError from '../ui/InlineError'
import Loading from '../ui/Loading'
import { useToast } from '../ui/ToastProvider'
import { GoogleLogin } from '@react-oauth/google'
import { kakaoLogin } from '../lib/kakao'
import { naverLogin } from '../lib/naver'
import { Link, useSearchParams } from 'react-router-dom'
import MenuGuideButton from '../components/MenuGuideButton'
import { ENABLE_USER_BILLING } from '../config/featureFlags'

const SHOW_WORK_STATUS_AND_ORG_SECTION = false
const SHOW_CALENDAR_NOTIFICATION_SETTINGS = false

export default function ProfilePage() {
    const { logout, user: currentUser } = useAuth()
    const { show } = useToast()
    const qc = useQueryClient()
    const [searchParams, setSearchParams] = useSearchParams()
    const accountDeletionSectionRef = useRef(null)
    const meQuery = useQuery({
        queryKey: ['me'],
        queryFn: async () => {
            const res = await api.get('/users/me')
            return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        }
    })

    const socialLinksQuery = useQuery({
        queryKey: ['me', 'socialLinks'],
        queryFn: async () => {
            const res = await api.get('/users/me/social-links')
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            return Array.isArray(payload) ? payload : []
        }
    })

    const proxyLinkCandidatesQuery = useQuery({
        queryKey: ['me', 'proxyLinkCandidates'],
        queryFn: async () => {
            const res = await api.get('/users/me/proxy-link-candidates')
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            return Array.isArray(payload) ? payload : []
        },
        enabled: String(meQuery.data?.orgType || '').toUpperCase() === 'PERSONAL',
    })

    const proxyLinksQuery = useQuery({
        queryKey: ['me', 'proxyLinks'],
        queryFn: async () => {
            const res = await api.get('/users/me/proxy-links')
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            return Array.isArray(payload) ? payload : []
        },
        enabled: String(meQuery.data?.orgType || '').toUpperCase() === 'PERSONAL',
    })

    const billingSummaryQuery = useQuery({
        queryKey: ['me', 'billingSummary'],
        queryFn: async () => {
            const res = await api.get('/billing/me', { __silent: true })
            return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        },
        retry: false,
    })

    const withdrawEligibilityQuery = useQuery({
        queryKey: ['me', 'withdrawalEligibility'],
        queryFn: async () => {
            const res = await api.get('/users/me/withdrawal-eligibility', { __silent: true })
            return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        },
        enabled: Boolean(meQuery.isSuccess && meQuery.data && String(meQuery.data.status || '').toUpperCase() === 'ACTIVE'),
        retry: false,
    })

    // 상담 신청 내역은 MVP에서는 내 정보에서 숨김(불필요)

    const [name, setName] = useState('')
    const [phoneNumber, setPhoneNumber] = useState('')
    const [jobTitle, setJobTitle] = useState('')
    const [signatureFileId, setSignatureFileId] = useState('')
    const [signaturePreviewUrl, setSignaturePreviewUrl] = useState('')
    const [signatureUploading, setSignatureUploading] = useState(false)
    const [signaturePadOpen, setSignaturePadOpen] = useState(false)
    const [signatureHasInk, setSignatureHasInk] = useState(false)
    const [profileImageFileId, setProfileImageFileId] = useState('')
    const [profilePortraitImageFileId, setProfilePortraitImageFileId] = useState('')
    const [profilePortraitCutoutFileId, setProfilePortraitCutoutFileId] = useState('')
    const [profilePortraitCutoutStatus, setProfilePortraitCutoutStatus] = useState('NONE')
    const [profileImageUploadingSlot, setProfileImageUploadingSlot] = useState('')
    const [profileCutoutGenerating, setProfileCutoutGenerating] = useState(false)
    const [profileImagePreview, setProfileImagePreview] = useState(null)
    const [status, setStatus] = useState('ACTIVE') // 계정 상태 (표시용)
    const [availability, setAvailability] = useState('ON_DUTY') // 근무 상태
    const [orgId, setOrgId] = useState('')
    const [orgOptions, setOrgOptions] = useState([])
    const [originalOrgId, setOriginalOrgId] = useState('')
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [nextOrgId, setNextOrgId] = useState('')
    const [notifyLoading, setNotifyLoading] = useState(false)
    const [notifyMessage, setNotifyMessage] = useState('')
    const signatureCanvasRef = useRef(null)
    const signatureCtxRef = useRef(null)
    const signatureDrawingRef = useRef(false)
    const signatureLastPointRef = useRef({ x: 0, y: 0 })
    const signatureHasInkRef = useRef(false)

    // 비밀번호 변경(팝업)
    const [pwOpen, setPwOpen] = useState(false)
    const [pwCurrent, setPwCurrent] = useState('')
    const [pwNext, setPwNext] = useState('')
    const [pwNextConfirm, setPwNextConfirm] = useState('')

    const [withdrawOpen, setWithdrawOpen] = useState(false)
    const [withdrawReqOpen, setWithdrawReqOpen] = useState(false)
    const [wdPassword, setWdPassword] = useState('')
    const [wdAckPrepaid, setWdAckPrepaid] = useState(false)
    const [wdAckIrrev, setWdAckIrrev] = useState(false)
    const [wdReqReason, setWdReqReason] = useState('')

    // App Store: 계정 삭제는 웹으로 안내할 때 삭제 완료 페이지로의 직접 링크가 필요함 → /profile?accountDeletion=1
    useEffect(() => {
        if (searchParams.get('accountDeletion') !== '1') return
        if (!meQuery.isSuccess || !meQuery.data) return
        if (String(meQuery.data.status || '').toUpperCase() !== 'ACTIVE') return
        if (withdrawEligibilityQuery.isLoading) return
        if (withdrawEligibilityQuery.isError) return

        const next = new URLSearchParams(searchParams)
        next.delete('accountDeletion')
        setSearchParams(next, { replace: true })

        const run = () => {
            accountDeletionSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            if (withdrawEligibilityQuery.data?.requiresApproval) {
                setWithdrawReqOpen(true)
                setWdPassword('')
                setWdReqReason('')
            } else {
                setWithdrawOpen(true)
                setWdPassword('')
                setWdAckPrepaid(false)
                setWdAckIrrev(false)
            }
        }
        requestAnimationFrame(run)
    }, [
        searchParams,
        setSearchParams,
        meQuery.isSuccess,
        meQuery.data,
        withdrawEligibilityQuery.isLoading,
        withdrawEligibilityQuery.isError,
        withdrawEligibilityQuery.data,
    ])

    // 권한 가드: 지점 관리자 이상만 소속 변경 가능
    const canChangeOrg = useMemo(() => {
        const roles = currentUser?.roles || []
        return roles.includes(Roles.OPERATOR) || roles.includes(Roles.GA_LEADER) || roles.includes(Roles.INSURER_LEADER)
    }, [currentUser])

    const canTestNotification = useMemo(() => {
        const roles = currentUser?.roles || []
        if (!Array.isArray(roles)) return false
        return roles.includes(Roles.OPERATOR)
            // || roles.includes(Roles.GA_LEADER)
            // || roles.includes(Roles.GA_BRANCH_MANAGER)
            // || roles.includes(Roles.GA_DEPUTY_BRANCH_MANAGER)
            // || roles.includes(Roles.INSURER_LEADER)
            // || roles.includes(Roles.INSURER_BRANCH_MANAGER)
            // || roles.includes(Roles.INSURER_DEPUTY_BRANCH_MANAGER)
    }, [currentUser])

    const isGaPlanner = useMemo(() => {
        const roles = currentUser?.roles || []
        return Array.isArray(roles) && roles.includes(Roles.GA_PLANNER)
    }, [currentUser])

    // 근무 상태 기간/대직자
    const [availStart] = useState('')
    const [availEnd] = useState('')
    const [deputyCandidateOptions, setDeputyCandidateOptions] = useState([])
    const [deputyUserId1, setDeputyUserId1] = useState('')
    const [deputyUserId2, setDeputyUserId2] = useState('')

    React.useEffect(() => {
        if (meQuery.data) {
            setName(meQuery.data.name || '')
            setPhoneNumber(meQuery.data.phoneNumber || '')
            setJobTitle(meQuery.data.jobTitle || '')
            setSignatureFileId(meQuery.data.signatureFileId || '')
            setProfileImageFileId(meQuery.data.profileImageFileId || meQuery.data.profileCardImageFileId || '')
            setProfilePortraitImageFileId(meQuery.data.profilePortraitImageFileId || '')
            setProfilePortraitCutoutFileId(meQuery.data.profilePortraitCutoutFileId || '')
            setProfilePortraitCutoutStatus(meQuery.data.profilePortraitCutoutStatus || 'NONE')
            setStatus(meQuery.data.status || 'ACTIVE')
            setAvailability(meQuery.data.availabilityStatus || 'ON_DUTY')
            const currentOrgId = String(meQuery.data.orgId || '')
            setOrgId(currentOrgId)
            setOriginalOrgId(currentOrgId)
        }
    }, [meQuery.data])

    React.useEffect(() => {
        if (!SHOW_WORK_STATUS_AND_ORG_SECTION) return
        let ignore = false
        async function loadOrgs() {
            try {
                const res = await api.get('/orgs/options')
                const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                if (!ignore) setOrgOptions(Array.isArray(payload) ? payload : [])
            } catch { }
        }
        loadOrgs()
        return () => { ignore = true }
    }, [])

    // 동일 조직 대직자 후보 로딩 (선택된 org 기준)
    React.useEffect(() => {
        if (!SHOW_WORK_STATUS_AND_ORG_SECTION) return
        let ignore = false
        async function loadDeputies() {
            try {
                if (isGaPlanner) {
                    if (!ignore) setDeputyCandidateOptions([])
                    return
                }
                if (!orgId) {
                    if (!ignore) setDeputyCandidateOptions([])
                    return
                }
                const params = { orgId: Number(orgId), size: 100 }
                const res = await api.get('/users', { params })
                const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                const list = Array.isArray(payload) ? payload : (payload?.content || payload?.items || [])
                const mapped = list
                    .filter(u => String(u.id) !== String(meQuery.data?.id))
                    .map(u => ({ id: u.id, label: `${u.name || u.email} (${u.email})` }))
                if (!ignore) {
                    setDeputyCandidateOptions(mapped)
                }
            } catch {
                if (!ignore) setDeputyCandidateOptions([])
            }
        }
        loadDeputies()
        return () => { ignore = true }
    }, [orgId, meQuery.data, isGaPlanner])

    // 조직 변경 시 후보에 없는 대직자는 자동 해제 + 중복 방지
    React.useEffect(() => {
        const isOk = (val) => !val || deputyCandidateOptions.some(o => String(o.id) === String(val))
        if (!isOk(deputyUserId1)) setDeputyUserId1('')
        if (!isOk(deputyUserId2)) setDeputyUserId2('')
        if (deputyUserId1 && deputyUserId2 && String(deputyUserId1) === String(deputyUserId2)) setDeputyUserId2('')
    }, [deputyCandidateOptions, deputyUserId1, deputyUserId2])

    const updateMutation = useMutation({
        mutationFn: async (payload) => {
            const needsDeputy = !isGaPlanner && (payload.availabilityStatus === 'VACATION' || payload.availabilityStatus === 'AWAY' || payload.availabilityStatus === 'TRAINING')
            const ids = [deputyUserId1, deputyUserId2].filter(Boolean).map(String)
            const uniqueIds = Array.from(new Set(ids))
            if (needsDeputy && uniqueIds.length === 0) {
                throw new Error('휴가/부재중일 때는 대직자를 최소 1명 선택해야 합니다')
            }
            await api.put('/users/me', {
                name: payload.name,
                phoneNumber: payload.phoneNumber,
                jobTitle: payload.jobTitle,
                signatureFileId: payload.signatureFileId,
                orgId: payload.orgId,
            })
            if (payload.availabilityStatus) {
                const me = meQuery.data
                const deputyUserIds = uniqueIds.map(n => Number(n))
                const body = {
                    userId: me.id,
                    status: payload.availabilityStatus,
                    startAt: payload.startAt ? payload.startAt : new Date().toISOString(),
                    endAt: payload.endAt || null,
                    deputyUserId: deputyUserIds.length > 0 ? deputyUserIds[0] : null, // 하위호환
                    deputyUserIds,
                }
                await api.post('/manager-status', body)
            }
            const res = await api.get('/users/me')
            return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        },
        onSuccess: (updated) => {
            localStorage.setItem('auth.user', JSON.stringify(updated))
            qc.invalidateQueries({ queryKey: ['me'] })
            try { window.dispatchEvent(new CustomEvent('availability:changed')); } catch { }
            show('저장 완료', 'success')
        },
        onError: (e) => {
            try { window.dispatchEvent(new CustomEvent('availability:failed')); } catch { }
            const msg = e?.response?.data?.message || e?.message || '저장 실패'
            show(msg, 'error')
        }
    })

    const refreshMeProfile = async () => {
        const res = await api.get('/users/me')
        const updated = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        try {
            localStorage.setItem('auth.user', JSON.stringify(updated))
        } catch { }
        qc.invalidateQueries({ queryKey: ['me'] })
        return updated
    }

    /** 서명 파일 업로드 후 계정에 반영 (기존에는 하단 '저장'을 눌러야 DB에 들어가 재방문 시 사라짐) */
    const persistSignatureFileIdToMe = async (fileId) => {
        const id = String(fileId || '').trim()
        if (!id) throw new Error('fileId missing')
        await api.put('/users/me', { signatureFileId: id })
        return refreshMeProfile()
    }

    const syncProfileImageState = (updated) => {
        setProfileImageFileId(updated?.profileImageFileId || updated?.profileCardImageFileId || '')
        setProfilePortraitImageFileId(updated?.profilePortraitImageFileId || '')
        setProfilePortraitCutoutFileId(updated?.profilePortraitCutoutFileId || '')
        setProfilePortraitCutoutStatus(updated?.profilePortraitCutoutStatus || 'NONE')
    }

    const persistProfileImageFieldsToMe = async (payload) => {
        await api.put('/users/me', payload)
        const updated = await refreshMeProfile()
        syncProfileImageState(updated)
        return updated
    }

    const uploadProfileImage = async (slot, e) => {
        const file = e.target.files?.[0]
        if (!file) return
        if (!String(file.type || '').startsWith('image/')) {
            show('사진은 이미지 파일로 업로드해 주세요.', 'error')
            e.target.value = ''
            return
        }
        setProfileImageUploadingSlot(slot)
        try {
            const resized = await resizeForProfileUpload(file, {
                maxSide: slot === 'portrait' ? 1800 : 1024,
                quality: slot === 'portrait' ? 0.86 : 0.82,
                preferWebp: false,
                maxBytes: slot === 'portrait' ? 5 * 1024 * 1024 : 3 * 1024 * 1024,
            })
            const formData = new FormData()
            formData.append('file', resized)
            const res = await api.post('/files/upload/public-profile-image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const fileId = payload?.fileId || payload?.id
            if (!fileId) throw new Error('fileId missing')
            const fieldPayload = slot === 'portrait'
                ? { profilePortraitImageFileId: fileId }
                : { profileImageFileId: fileId }
            const updated = await persistProfileImageFieldsToMe(fieldPayload)
            syncProfileImageState(updated)
            show('사진이 내 정보에 저장되었습니다.', 'success')
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || '사진 업로드 또는 저장에 실패했습니다.'
            show(msg, 'error')
        } finally {
            setProfileImageUploadingSlot('')
            e.target.value = ''
        }
    }

    const clearProfileImageField = async (field) => {
        try {
            const updated = await persistProfileImageFieldsToMe({ [field]: '' })
            syncProfileImageState(updated)
            show('사진이 제거되었습니다.', 'success')
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || '사진 제거에 실패했습니다.'
            show(msg, 'error')
        }
    }

    const generateProfilePortraitCutout = async () => {
        setProfileCutoutGenerating(true)
        try {
            const res = await api.post('/users/me/profile-images/portrait-cutout')
            const updated = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            syncProfileImageState(updated)
            try {
                localStorage.setItem('auth.user', JSON.stringify({ ...(meQuery.data || {}), ...updated }))
            } catch { }
            qc.invalidateQueries({ queryKey: ['me'] })
            show('리포트용 사진이 생성되었습니다.', 'success')
        } catch (err) {
            const updated = await refreshMeProfile().catch(() => null)
            if (updated) syncProfileImageState(updated)
            const msg = err?.response?.data?.message || err?.message || '리포트용 사진 생성에 실패했습니다.'
            show(msg, 'error')
        } finally {
            setProfileCutoutGenerating(false)
        }
    }

    const changePasswordMutation = useMutation({
        mutationFn: async ({ currentPassword, newPassword, newPasswordConfirm }) => {
            const res = await api.post('/users/me/change-password', {
                currentPassword,
                newPassword,
                newPasswordConfirm
            })
            return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        },
        onSuccess: () => {
            setPwOpen(false)
            setPwCurrent('')
            setPwNext('')
            setPwNextConfirm('')
            show('비밀번호가 변경되었습니다', 'success')
        },
        onError: (e) => {
            const msg = e?.response?.data?.message || e?.message || '비밀번호 변경에 실패했습니다'
            show(msg, 'error')
        }
    })

    const uploadSignatureImage = async (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        if (!String(file.type || '').startsWith('image/')) {
            show('서명은 이미지 파일로 업로드해 주세요.', 'error')
            e.target.value = ''
            return
        }
        setSignatureUploading(true)
        try {
            const formData = new FormData()
            formData.append('file', file)
            const res = await api.post('/files', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const fileId = payload?.fileId || payload?.id
            if (!fileId) throw new Error('fileId missing')
            const updated = await persistSignatureFileIdToMe(fileId)
            setSignatureFileId(updated?.signatureFileId || fileId)
            show('서명이 내 정보에 저장되었습니다.', 'success')
        } catch {
            show('서명 이미지 업로드 또는 저장에 실패했습니다.', 'error')
        } finally {
            setSignatureUploading(false)
            e.target.value = ''
        }
    }

    const setupSignatureCanvas = React.useCallback(() => {
        const canvas = signatureCanvasRef.current
        if (!canvas) return
        const parent = canvas.parentElement
        const cssW = Math.max(280, parent ? parent.clientWidth : canvas.clientWidth || 320)
        const cssH = Math.max(160, Math.min(260, Math.round(cssW * 0.42)))
        const dpr = window.devicePixelRatio || 1

        canvas.style.width = `${cssW}px`
        canvas.style.height = `${cssH}px`
        canvas.width = Math.floor(cssW * dpr)
        canvas.height = Math.floor(cssH * dpr)

        const ctx = canvas.getContext('2d')
        if (!ctx) return
        signatureCtxRef.current = ctx
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        ctx.lineWidth = 3
        ctx.strokeStyle = '#0f172a'
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, cssW, cssH)
        signatureHasInkRef.current = false
        setSignatureHasInk(false)
    }, [])

    React.useEffect(() => {
        if (!signaturePadOpen) return
        setupSignatureCanvas()
        const onResize = () => setupSignatureCanvas()
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [signaturePadOpen, setupSignatureCanvas])

    React.useEffect(() => {
        let objectUrl = ''
        let ignore = false
        ;(async () => {
            if (!signatureFileId) {
                setSignaturePreviewUrl('')
                return
            }
            try {
                const res = await api.get(`/files/${encodeURIComponent(signatureFileId)}/preview`, {
                    responseType: 'blob',
                    __silent: true,
                })
                if (ignore) return
                objectUrl = URL.createObjectURL(res.data)
                setSignaturePreviewUrl(objectUrl)
            } catch {
                if (!ignore) setSignaturePreviewUrl('')
            }
        })()
        return () => {
            ignore = true
            if (objectUrl) URL.revokeObjectURL(objectUrl)
        }
    }, [signatureFileId])

    const clearSignatureCanvas = React.useCallback(() => {
        const canvas = signatureCanvasRef.current
        const ctx = signatureCtxRef.current
        if (!canvas || !ctx) return
        const w = canvas.clientWidth
        const h = canvas.clientHeight
        ctx.clearRect(0, 0, w, h)
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, w, h)
        signatureHasInkRef.current = false
        setSignatureHasInk(false)
    }, [])

    const getSignaturePoint = (e) => {
        const canvas = signatureCanvasRef.current
        if (!canvas) return { x: 0, y: 0 }
        const rect = canvas.getBoundingClientRect()
        return {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        }
    }

    const onSignaturePointerDown = (e) => {
        const canvas = signatureCanvasRef.current
        const ctx = signatureCtxRef.current
        if (!canvas || !ctx) return
        try { e.preventDefault() } catch { }
        signatureDrawingRef.current = true
        const p = getSignaturePoint(e)
        signatureLastPointRef.current = p
        try { canvas.setPointerCapture(e.pointerId) } catch { }
        ctx.beginPath()
        ctx.moveTo(p.x, p.y)
        ctx.lineTo(p.x + 0.01, p.y + 0.01)
        ctx.stroke()
        if (!signatureHasInkRef.current) {
            signatureHasInkRef.current = true
            setSignatureHasInk(true)
        }
    }

    const onSignaturePointerMove = (e) => {
        const ctx = signatureCtxRef.current
        if (!signatureDrawingRef.current || !ctx) return
        try { e.preventDefault() } catch { }
        const p = getSignaturePoint(e)
        const last = signatureLastPointRef.current
        const midX = (last.x + p.x) / 2
        const midY = (last.y + p.y) / 2
        ctx.quadraticCurveTo(last.x, last.y, midX, midY)
        ctx.stroke()
        signatureLastPointRef.current = p
    }

    const endSignatureDrawing = (e) => {
        const canvas = signatureCanvasRef.current
        const ctx = signatureCtxRef.current
        if (!canvas || !ctx || !signatureDrawingRef.current) return
        try { e.preventDefault() } catch { }
        signatureDrawingRef.current = false
        ctx.closePath()
        try { canvas.releasePointerCapture(e.pointerId) } catch { }
    }

    const saveDrawnSignature = async () => {
        const canvas = signatureCanvasRef.current
        if (!canvas) return
        if (!signatureHasInkRef.current) {
            show('서명을 먼저 입력해 주세요.', 'error')
            return
        }
        setSignatureUploading(true)
        try {
            const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
            if (!blob) throw new Error('signature blob missing')
            const file = new File([blob], 'signature.png', { type: 'image/png' })
            const formData = new FormData()
            formData.append('file', file)
            const res = await api.post('/files', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const fileId = payload?.fileId || payload?.id
            if (!fileId) throw new Error('fileId missing')
            const updated = await persistSignatureFileIdToMe(fileId)
            setSignatureFileId(updated?.signatureFileId || fileId)
            setSignaturePadOpen(false)
            show('서명이 내 정보에 저장되었습니다.', 'success')
        } catch {
            show('서명 저장에 실패했습니다.', 'error')
        } finally {
            setSignatureUploading(false)
        }
    }

    const withdrawMutation = useMutation({
        mutationFn: async () => {
            const res = await api.post('/users/me/withdrawal', {
                currentPassword: wdPassword,
                acknowledgePrepaidForfeit: wdAckPrepaid,
                acknowledgeIrreversible: wdAckIrrev,
            })
            return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        },
        onSuccess: async () => {
            show('탈퇴 처리되었습니다.', 'success')
            setWithdrawOpen(false)
            setWdPassword('')
            setWdAckPrepaid(false)
            setWdAckIrrev(false)
            await logout()
        },
        onError: (e) => {
            const msg = e?.response?.data?.message || e?.message || '탈퇴 처리에 실패했습니다.'
            show(msg, 'error')
        },
    })

    const withdrawRequestMutation = useMutation({
        mutationFn: async () => {
            const res = await api.post('/users/me/withdrawal-request', {
                currentPassword: wdPassword,
                reason: wdReqReason,
            })
            return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        },
        onSuccess: () => {
            show('탈퇴 승인 요청이 접수되었습니다. 운영 검토 후 처리됩니다.', 'success')
            setWithdrawReqOpen(false)
            setWdPassword('')
            setWdReqReason('')
        },
        onError: (e) => {
            const msg = e?.response?.data?.message || e?.message || '요청에 실패했습니다.'
            show(msg, 'error')
        },
    })

    const triggerNotificationTest = async (kind) => {
        setNotifyLoading(true)
        setNotifyMessage('')
        try {
            const res = await api.post(`/notification-test/${kind}`)
            const data = res?.data?.data ?? res?.data
            setNotifyMessage(data?.title ? `알림 전송 완료: ${data.title}` : '알림 전송 완료')
        } catch (e) {
            const msg = e?.response?.data?.message || e.message || '알림 전송에 실패했습니다.'
            setNotifyMessage(msg)
        } finally {
            setNotifyLoading(false)
        }
    }

    const linkedProviders = useMemo(() => {
        const set = new Set()
        for (const row of (socialLinksQuery.data || [])) {
            const p = row?.provider
            if (p) set.add(String(p).toUpperCase())
        }
        return set
    }, [socialLinksQuery.data])

    const googleEnabled = String(process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim().length > 0
    const kakaoEnabled = String(process.env.REACT_APP_KAKAO_JS_KEY || '').trim().length > 0
    const naverEnabled = String(process.env.REACT_APP_NAVER_CLIENT_ID || '').trim().length > 0
    const socialConfigured = googleEnabled || kakaoEnabled || naverEnabled
    const isOperator = useMemo(() => {
        const roles = currentUser?.roles || []
        return Array.isArray(roles) && roles.includes(Roles.OPERATOR)
    }, [currentUser])
    // 키 미설정 상태에서는 운영자만 소셜 UI 노출 (단, 이미 연동된 계정은 해제할 수 있도록 예외 허용)
    const showSocialUi = socialConfigured || isOperator || linkedProviders.size > 0

    const unlinkProvider = async (provider) => {
        try {
            await api.delete(`/users/me/social-links/${provider}`)
            qc.invalidateQueries({ queryKey: ['me', 'socialLinks'] })
            show('연동이 해제되었습니다', 'success')
        } catch (e) {
            const msg = e?.response?.data?.data?.message || e?.response?.data?.message || e?.message || '연동 해제에 실패했습니다'
            show(msg, 'error')
        }
    }

    const linkProxyUser = async (proxyUserId) => {
        try {
            await api.post(`/users/me/proxy-links/${proxyUserId}`)
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['me', 'proxyLinks'] }),
                qc.invalidateQueries({ queryKey: ['me', 'proxyLinkCandidates'] }),
            ])
            show('프록시 계정 연결이 완료되었습니다', 'success')
        } catch (e) {
            const msg = e?.response?.data?.message || e?.message || '프록시 계정 연결에 실패했습니다'
            show(msg, 'error')
        }
    }

    if (meQuery.isLoading) return <Loading />
    if (meQuery.isError) return <InlineError message="내 정보를 불러오지 못했습니다" />
    const me = meQuery.data || {}
    const billingSummary = billingSummaryQuery.data || {}
    const canUseBilling = Array.isArray(me.capabilities) && me.capabilities.includes('BILLING')
    const showBillingProfileSection = (
        canUseBilling
        || billingSummaryQuery.isLoading
        || Boolean(billingSummary?.billingReady)
        || billingSummary?.orgContractPrimary === true
    )
    const profileSubscription = billingSummary.subscription || {}
    const profileCurrentQuotaPeriod = profileSubscription.currentQuotaPeriod || null
    const profileQuotaCounterMap = new Map(
        Array.isArray(profileCurrentQuotaPeriod?.counters)
            ? profileCurrentQuotaPeriod.counters.map((item) => [item.featureCode, item])
            : []
    )
    const profileFeatures = orderedProfileBillingFeatures(
        Array.isArray(billingSummary.features) ? billingSummary.features.filter((feature) => feature?.enabled !== false) : []
    )
    const orgContractPrimary = billingSummary.orgContractPrimary === true
    const profileSubscriptionPlanLabel = orgContractPrimary
        ? (billingSummary.orgContract?.planName || formatProfilePlanCodeLabel(billingSummary.orgContract?.appliedPlanCode) || billingSummary.quote?.planName || '조직 계약')
        : (profileSubscription.planName || billingSummary.plan?.name || billingSummary.quote?.planName || '-')
    const profileSubscriptionEndsAt = orgContractPrimary
        ? billingSummary.orgContract?.endsAt
        : (profileSubscription.endsAt || profileCurrentQuotaPeriod?.periodEndAt)
    const isPersonalProfile = String(me.orgType || '').toUpperCase() === 'PERSONAL'
    const proxyLinks = Array.isArray(proxyLinksQuery.data) ? proxyLinksQuery.data : []
    const proxyLinkCandidates = Array.isArray(proxyLinkCandidatesQuery.data) ? proxyLinkCandidatesQuery.data : []
    const showProxyLinkSection = isPersonalProfile && proxyLinkCandidates.length > 0
    const profileCardPreviewUrl = profileImageFileId ? resolveApiAssetUrl(profileImageFileId) : ''
    const profilePortraitPreviewUrl = profilePortraitImageFileId ? resolveApiAssetUrl(profilePortraitImageFileId) : ''
    const profileCutoutPreviewUrl = profilePortraitCutoutFileId ? resolveApiAssetUrl(profilePortraitCutoutFileId) : ''
    const profileCutoutStatusMeta = getProfileCutoutStatusMeta(profilePortraitCutoutStatus)
    const canGenerateProfileCutout = Boolean(profilePortraitImageFileId || profileImageFileId)
    const openProfileImagePreview = (title, src) => {
        if (!src) return
        setProfileImagePreview({ title, src })
    }
    const profileImagesSection = (
        <div className="rounded border p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="font-semibold text-slate-900">사진</h2>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">
                        명함과 리포트에 사용할 사진을 관리합니다.
                    </p>
                </div>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3">
                <div className="rounded border bg-white p-3">
                    <div className="flex items-start gap-3">
                        {profileCardPreviewUrl ? (
                            <button
                                type="button"
                                className="flex h-20 w-20 shrink-0 cursor-zoom-in items-center justify-center overflow-hidden rounded-full border bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-navy focus:ring-offset-2"
                                onClick={() => openProfileImagePreview('명함용 사진', profileCardPreviewUrl)}
                                aria-label="명함용 사진 크게 보기"
                            >
                                <img
                                    src={profileCardPreviewUrl}
                                    alt="명함용 사진"
                                    className="h-full w-full object-cover object-center"
                                    decoding="async"
                                    loading="lazy"
                                />
                            </button>
                        ) : (
                            <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border bg-slate-50">
                                <span className="text-xs text-slate-400">없음</span>
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-slate-800">명함용 사진</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                <label className={`cursor-pointer rounded border bg-white px-3 py-2 text-sm hover:bg-slate-50 ${profileImageUploadingSlot ? 'pointer-events-none opacity-60' : ''}`}>
                                    {profileImageUploadingSlot === 'card' ? '업로드 중...' : (profileImageFileId ? '변경' : '업로드')}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => uploadProfileImage('card', e)}
                                        disabled={Boolean(profileImageUploadingSlot)}
                                    />
                                </label>
                                {profileImageFileId ? (
                                    <button
                                        type="button"
                                        className="rounded border border-rose-200 px-3 py-2 text-sm text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                                        onClick={() => clearProfileImageField('profileImageFileId')}
                                        disabled={Boolean(profileImageUploadingSlot) || profileCutoutGenerating}
                                    >
                                        제거
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="rounded border bg-white p-3">
                    <div className="flex items-start gap-3">
                        {profilePortraitPreviewUrl ? (
                            <button
                                type="button"
                                className="flex h-28 w-20 shrink-0 cursor-zoom-in items-center justify-center overflow-hidden rounded border bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-navy focus:ring-offset-2"
                                onClick={() => openProfileImagePreview('리포트용 사진', profilePortraitPreviewUrl)}
                                aria-label="리포트용 사진 크게 보기"
                            >
                                <img
                                    src={profilePortraitPreviewUrl}
                                    alt="리포트용 사진"
                                    className="h-full w-full object-cover object-top"
                                    decoding="async"
                                    loading="lazy"
                                />
                            </button>
                        ) : (
                            <div className="flex h-28 w-20 shrink-0 items-center justify-center overflow-hidden rounded border bg-slate-50">
                                <span className="text-xs text-slate-400">없음</span>
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-slate-800">리포트용 사진</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                <label className={`cursor-pointer rounded border bg-white px-3 py-2 text-sm hover:bg-slate-50 ${profileImageUploadingSlot ? 'pointer-events-none opacity-60' : ''}`}>
                                    {profileImageUploadingSlot === 'portrait' ? '업로드 중...' : (profilePortraitImageFileId ? '변경' : '업로드')}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => uploadProfileImage('portrait', e)}
                                        disabled={Boolean(profileImageUploadingSlot)}
                                    />
                                </label>
                                {profilePortraitImageFileId ? (
                                    <button
                                        type="button"
                                        className="rounded border border-rose-200 px-3 py-2 text-sm text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                                        onClick={() => clearProfileImageField('profilePortraitImageFileId')}
                                        disabled={Boolean(profileImageUploadingSlot) || profileCutoutGenerating}
                                    >
                                        제거
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="mt-3 rounded border bg-slate-50 p-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="text-sm font-semibold text-slate-800">리포트용 사진 생성</div>
                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${profileCutoutStatusMeta.className}`}>
                                {profileCutoutStatusMeta.label}
                            </span>
                        </div>
                        <div className="mt-1 text-xs text-slate-500">리포트와 안내 이미지에 사용할 배경 제거 사진입니다.</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            className="rounded border bg-white px-3 py-2 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={generateProfilePortraitCutout}
                            disabled={!canGenerateProfileCutout || profileCutoutGenerating || Boolean(profileImageUploadingSlot)}
                        >
                            {profileCutoutGenerating ? '생성 중...' : (profilePortraitCutoutFileId ? '다시 생성' : '리포트용 사진 생성')}
                        </button>
                        {profilePortraitCutoutFileId ? (
                            <button
                                type="button"
                                className="rounded border border-rose-200 bg-white px-3 py-2 text-sm text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                                onClick={() => clearProfileImageField('profilePortraitCutoutFileId')}
                                disabled={profileCutoutGenerating || Boolean(profileImageUploadingSlot)}
                            >
                                제거
                            </button>
                        ) : null}
                    </div>
                </div>
                {profileCutoutPreviewUrl ? (
                    <button
                        type="button"
                        className="mt-3 inline-flex min-h-[120px] min-w-[96px] cursor-zoom-in items-center justify-center rounded border bg-white p-2 focus:outline-none focus:ring-2 focus:ring-brand-navy focus:ring-offset-2"
                        onClick={() => openProfileImagePreview('생성된 리포트용 사진', profileCutoutPreviewUrl)}
                        aria-label="생성된 리포트용 사진 크게 보기"
                    >
                        <img
                            src={profileCutoutPreviewUrl}
                            alt="리포트용 사진"
                            className="max-h-36 max-w-28 object-contain"
                            decoding="async"
                            loading="lazy"
                        />
                    </button>
                ) : (
                    <div className="mt-3 rounded border border-dashed bg-white px-3 py-4 text-sm text-slate-500">
                        생성된 리포트용 사진이 없습니다.
                    </div>
                )}
            </div>
        </div>
    )
    const signatureSection = (
        <div className="rounded border p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="font-semibold text-slate-900">서명</h2>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">
                        3종확인서 등 모집자 서명이 필요한 서식에 자동 입력할 이미지입니다.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        className="rounded border bg-white px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-60"
                        onClick={() => setSignaturePadOpen((v) => !v)}
                        disabled={signatureUploading}
                    >
                        {signaturePadOpen ? '직접 서명 닫기' : '직접 서명하기'}
                    </button>
                    <label className={`rounded border bg-white px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer ${signatureUploading ? 'opacity-60 pointer-events-none' : ''}`}>
                        {signatureUploading ? '업로드 중...' : '이미지 업로드'}
                        <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={uploadSignatureImage}
                            disabled={signatureUploading}
                        />
                    </label>
                </div>
            </div>
            {signaturePadOpen && (
                <div className="mt-3 rounded border bg-white p-3">
                    <div className="flex items-center justify-between gap-2">
                        <div>
                            <div className="text-sm font-medium text-slate-800">직접 서명</div>
                            <div className="mt-0.5 text-xs text-slate-500">마우스 또는 터치로 아래 영역에 서명해 주세요.</div>
                        </div>
                        <button
                            type="button"
                            className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50"
                            onClick={clearSignatureCanvas}
                            disabled={signatureUploading}
                        >
                            지우기
                        </button>
                    </div>
                    <div className="mt-3 rounded-lg border bg-slate-50 p-2">
                        <canvas
                            ref={signatureCanvasRef}
                            className="w-full rounded bg-white"
                            style={{ touchAction: 'none' }}
                            onPointerDown={onSignaturePointerDown}
                            onPointerMove={onSignaturePointerMove}
                            onPointerUp={endSignatureDrawing}
                            onPointerCancel={endSignatureDrawing}
                            onPointerLeave={endSignatureDrawing}
                        />
                    </div>
                    {!signatureHasInk && (
                        <div className="mt-2 text-xs text-slate-500">서명 영역이 비어 있습니다.</div>
                    )}
                    <div className="mt-3 flex justify-end">
                        <button
                            type="button"
                            className="rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                            onClick={saveDrawnSignature}
                            disabled={signatureUploading || !signatureHasInk}
                        >
                            {signatureUploading ? '저장 중...' : '서명 이미지로 저장'}
                        </button>
                    </div>
                </div>
            )}
            {signatureFileId ? (
                <div className="mt-3 rounded border bg-white p-3">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex min-h-[72px] min-w-[180px] items-center justify-center rounded border bg-white px-3 py-2">
                            {signaturePreviewUrl ? (
                                <img
                                    src={signaturePreviewUrl}
                                    alt="저장된 서명"
                                    className="max-h-20 max-w-[220px] object-contain"
                                />
                            ) : (
                                <span className="text-xs text-slate-400">미리보기 준비 중</span>
                            )}
                        </div>
                        <button
                            type="button"
                            className="rounded border border-rose-200 px-3 py-2 text-sm text-rose-700 hover:bg-rose-50"
                            onClick={() => setSignatureFileId('')}
                        >
                            제거
                        </button>
                    </div>
                </div>
            ) : (
                <div className="mt-3 rounded border border-dashed bg-white px-3 py-4 text-sm text-slate-500">
                    등록된 서명이 없습니다.
                </div>
            )}
        </div>
    )

    return (
        <div className="w-full">
            <div className="mb-6 flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-semibold">내 정보</h1>
                <MenuGuideButton slug="profile" title="프로필·내 정보 가이드" />
            </div>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-stretch">
                <div className="flex min-w-0 flex-col gap-4">
                    <div className="flex items-center gap-2 px-1">
                        <span className="h-4 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
                        <div className="text-xs font-bold uppercase tracking-wide text-slate-500">기본 정보</div>
                    </div>
                    <div className="flex flex-1 flex-col gap-4">
                    <div className="rounded border p-4">
                        <div className="font-semibold mb-3 text-slate-900">계정</div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm text-slate-600">이메일</label>
                                <div className="mt-1 text-slate-800">{me.email}</div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm text-slate-600">권한</label>
                                    <div className="mt-1 text-slate-800 text-xs inline-flex flex-wrap gap-2">
                                        {(Array.isArray(me.roles) && me.roles.length > 0 ? me.roles : (Array.isArray(currentUser?.roles) ? currentUser.roles : [])).map((r, idx) => (
                                            <span key={`${r}-${idx}`} className="px-2 py-1 rounded border bg-slate-50 border-slate-200 text-slate-700">
                                                {RoleLabels?.[r] || r}
                                            </span>
                                        ))}
                                        {(!Array.isArray(me.roles) || me.roles.length === 0) && (!Array.isArray(currentUser?.roles) || currentUser.roles.length === 0) && (
                                            <span className="text-slate-500">-</span>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm text-slate-600">계정상태</label>
                                    <div className="mt-1 text-slate-800 text-xs inline-flex items-center gap-2">
                                        <span className={`px-2 py-1 rounded border ${status === 'ACTIVE' ? 'bg-green-50 border-green-200 text-green-700' : status === 'SUSPENDED' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>{status}</span>
                                        <span className="text-slate-500">(관리자만 수정 가능)</span>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm text-slate-600">이름</label>
                                    <input className="mt-1 w-full rounded border px-3 py-2" value={name} onChange={(e) => setName(e.target.value)} />
                                </div>
                                <div>
                                    <label className="block text-sm text-slate-600">직책</label>
                                    <input className="mt-1 w-full rounded border px-3 py-2" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="예: 팀장, 지점장" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm text-slate-600">전화번호</label>
                                <input className="mt-1 w-full rounded border px-3 py-2" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} />
                            </div>
                        </div>
                    </div>

                    <div>
                        <button type="button" className="rounded border px-4 py-2" onClick={() => setPwOpen(true)}>
                            비밀번호 변경
                        </button>
                    </div>
                    {signatureSection}
                    </div>
                </div>

                <div className="flex min-w-0 flex-col gap-4">
                    <div className="flex items-center gap-2 px-1">
                        <span className="h-4 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
                        <div className="text-xs font-bold uppercase tracking-wide text-slate-500">부가 정보</div>
                    </div>
                    <div className="flex flex-1 flex-col gap-4">
                    {profileImagesSection}
                    {showProxyLinkSection && (
                        <div className="rounded border p-4">
                            <h2 className="font-semibold mb-2">GA 대리 생성 계정 연결</h2>
                            <div className="text-xs text-slate-500 mb-3">
                                휴대폰 번호가 일치하는 GA 대리 생성 계정을 직접 선택해 연결할 수 있습니다. 연결 후 근무 상태 변경은 연결된 프록시 계정에도 함께 반영됩니다.
                            </div>
                            {proxyLinksQuery.isLoading ? (
                                <div className="text-sm text-slate-500">연결 정보를 불러오는 중...</div>
                            ) : proxyLinks.length > 0 ? (
                                <div className="mb-3 space-y-2">
                                    {proxyLinks.map((row) => (
                                        <div key={row.id} className="rounded border bg-slate-50 px-3 py-2 text-sm">
                                            <div className="font-medium text-slate-800">{row.proxyUserName || '-'}</div>
                                            <div className="text-xs text-slate-500">{row.proxyOrgName || '-'} / {row.proxyEmail || '-'}</div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="mb-3 text-sm text-slate-500">아직 연결된 프록시 계정이 없습니다.</div>
                            )}
                            {proxyLinkCandidatesQuery.isLoading ? (
                                <div className="text-sm text-slate-500">연결 가능한 계정을 찾는 중...</div>
                            ) : proxyLinkCandidates.length > 0 ? (
                                <div className="space-y-2">
                                    {proxyLinkCandidates.map((row) => (
                                        <div key={row.id} className="flex items-center justify-between gap-3 rounded border px-3 py-2">
                                            <div>
                                                <div className="text-sm font-medium text-slate-800">{row.name || '-'}</div>
                                                <div className="text-xs text-slate-500">{row.orgName || '-'} / {row.email || '-'}</div>
                                            </div>
                                            <button
                                                type="button"
                                                className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                                onClick={() => linkProxyUser(row.id)}
                                                disabled={Boolean(row.linked)}
                                            >
                                                {row.linked ? '연결됨' : '연결'}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-sm text-slate-500">일치하는 프록시 후보가 없습니다.</div>
                            )}
                        </div>
                    )}
                    {SHOW_WORK_STATUS_AND_ORG_SECTION && (
                    <div className="rounded border p-4">
                        <h2 className="font-semibold mb-3 text-slate-900">근무 상태 및 소속</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm text-slate-600">근무 상태</label>
                                <select className="mt-1 w-full rounded border px-3 py-2" value={availability} onChange={(e) => setAvailability(e.target.value)}>
                                    <option value="ON_DUTY">활성(근무 중)</option>
                                    <option value="VACATION">휴가</option>
                                    <option value="TRAINING">교육</option>
                                    <option value="AWAY">부재중</option>
                                </select>
                            </div>
                            {!isGaPlanner && (availability === 'VACATION' || availability === 'AWAY' || availability === 'TRAINING') && (
                                <div className="rounded border border-slate-100 bg-slate-50/80 p-3 space-y-3">
                                    <div className="text-xs font-medium text-slate-700">대직자 우선순위</div>
                                    <div className="text-xs text-slate-500">근무 가능은 ON_DUTY만이며, 1순위가 근무 불가면 2순위로 자동 전환됩니다.</div>
                                    <div>
                                        <label className="block text-sm text-slate-600">대직자 1순위(필수)</label>
                                        <select className={`mt-1 w-full rounded border px-3 py-2 bg-white ${!deputyUserId1 ? 'border-red-300' : ''}`} value={deputyUserId1} onChange={(e) => setDeputyUserId1(e.target.value)}>
                                            <option value="">선택</option>
                                            {deputyCandidateOptions.map(o => (<option key={o.id} value={String(o.id)}>{o.label}</option>))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm text-slate-600">대직자 2순위(선택)</label>
                                        <select className="mt-1 w-full rounded border px-3 py-2 bg-white" value={deputyUserId2} onChange={(e) => setDeputyUserId2(e.target.value)}>
                                            <option value="">선택 안 함</option>
                                            {deputyCandidateOptions
                                                .filter(o => String(o.id) !== String(deputyUserId1))
                                                .map(o => (<option key={o.id} value={String(o.id)}>{o.label}</option>))}
                                        </select>
                                    </div>
                                    {(availability === 'VACATION' || availability === 'AWAY' || availability === 'TRAINING') && !deputyUserId1 && (
                                        <div className="text-xs text-red-600">교육/휴가/부재중 상태에서는 대직자 1순위가 필수입니다.</div>
                                    )}
                                </div>
                            )}
                            {isGaPlanner && (availability === 'VACATION' || availability === 'AWAY' || availability === 'TRAINING') && (
                                <div className="text-xs text-slate-500">
                                    GA 설계사 역할은 설계의뢰 자동 배정 대상이 아니므로 대직자 설정이 제공되지 않습니다.
                                </div>
                            )}
                            <div>
                                <label className="block text-sm text-slate-600">소속 조직</label>
                                <select className="mt-1 w-full rounded border px-3 py-2" value={orgId} onChange={(e) => {
                                    const val = e.target.value
                                    if (val === orgId) return
                                    setNextOrgId(val)
                                    setConfirmOpen(true)
                                }} disabled={!canChangeOrg}>
                                    <option value="">선택</option>
                                    {orgOptions.map(o => (<option key={o.id} value={o.id}>{o.name} ({o.type})</option>))}
                                </select>
                                {!canChangeOrg && (<p className="text-xs text-slate-500 mt-1">소속 변경은 관리자(지점 리더 이상)만 가능합니다.</p>)}
                                <p className="text-xs text-slate-500 mt-1">변경 저장 시 아래 「저장」을 눌러야 적용됩니다.</p>
                            </div>
                        </div>
                    </div>
                    )}

                    {SHOW_CALENDAR_NOTIFICATION_SETTINGS && <CalendarNotificationSettings />}

                    {showSocialUi && (
                        <div className="rounded border p-4">
                            <h2 className="font-semibold mb-2">소셜 로그인 연동</h2>
                            <div className="text-xs text-slate-500 mb-3">
                                연동해두면 다음부터 구글/카카오로도 로그인할 수 있습니다.
                            </div>

                            {!socialConfigured && isOperator && (
                                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                                    현재 소셜 로그인 키가 설정되지 않아 <span className="font-semibold">운영자만</span> UI가 표시됩니다.
                                    <div className="mt-1 text-[11px] text-amber-800">
                                        필요 설정: <code className="px-1 py-0.5 bg-white rounded">REACT_APP_GOOGLE_CLIENT_ID</code>, <code className="px-1 py-0.5 bg-white rounded">REACT_APP_KAKAO_JS_KEY</code>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-3">
                                {(linkedProviders.has('GOOGLE') || googleEnabled || isOperator) && (
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <div className="text-sm font-medium">Google</div>
                                            <div className="text-xs text-slate-500">
                                                {linkedProviders.has('GOOGLE') ? '연동됨' : '미연동'}
                                            </div>
                                        </div>
                                        <div className="min-w-[220px]">
                                            {linkedProviders.has('GOOGLE') ? (
                                                <button className="rounded border px-3 py-2 text-sm hover:bg-slate-50" type="button" onClick={() => unlinkProvider('google')}>
                                                    연동 해제
                                                </button>
                                            ) : googleEnabled ? (
                                                <GoogleLogin
                                                    useOneTap={false}
                                                    onSuccess={async (cred) => {
                                                        try {
                                                            const idToken = cred?.credential
                                                            if (!idToken) throw new Error('구글 토큰을 받지 못했습니다.')
                                                            await api.post('/users/me/social-links/google', { idToken })
                                                            qc.invalidateQueries({ queryKey: ['me', 'socialLinks'] })
                                                            show('Google 연동이 완료되었습니다', 'success')
                                                        } catch (e) {
                                                            const msg = e?.response?.data?.data?.message || e?.response?.data?.message || e?.message || 'Google 연동에 실패했습니다'
                                                            show(msg, 'error')
                                                        }
                                                    }}
                                                    onError={() => show('Google 연동에 실패했습니다', 'error')}
                                                    width="220px"
                                                />
                                            ) : (
                                                isOperator ? <div className="text-xs text-slate-500">설정 필요: `REACT_APP_GOOGLE_CLIENT_ID`</div> : null
                                            )}
                                        </div>
                                    </div>
                                )}

                                {(linkedProviders.has('KAKAO') || kakaoEnabled || isOperator) && (
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <div className="text-sm font-medium">Kakao</div>
                                            <div className="text-xs text-slate-500">
                                                {linkedProviders.has('KAKAO') ? '연동됨' : '미연동'}
                                            </div>
                                        </div>
                                        <div className="min-w-[220px] flex justify-end">
                                            {linkedProviders.has('KAKAO') ? (
                                                <button className="rounded border px-3 py-2 text-sm hover:bg-slate-50" type="button" onClick={() => unlinkProvider('kakao')}>
                                                    연동 해제
                                                </button>
                                            ) : kakaoEnabled ? (
                                                <button
                                                    type="button"
                                                    className="rounded bg-[#FEE500] text-[#191919] px-3 py-2 text-sm font-semibold disabled:opacity-50"
                                                    onClick={async () => {
                                                        try {
                                                            const accessToken = await kakaoLogin({ scope: 'account_email' })
                                                            await api.post('/users/me/social-links/kakao', { accessToken })
                                                            qc.invalidateQueries({ queryKey: ['me', 'socialLinks'] })
                                                            show('Kakao 연동이 완료되었습니다', 'success')
                                                        } catch (e) {
                                                            const msg = e?.response?.data?.data?.message || e?.response?.data?.message || e?.message || 'Kakao 연동에 실패했습니다'
                                                            show(msg, 'error')
                                                        }
                                                    }}
                                                >
                                                    카카오 연동
                                                </button>
                                            ) : (
                                                isOperator ? <div className="text-xs text-slate-500">설정 필요: `REACT_APP_KAKAO_JS_KEY`</div> : null
                                            )}
                                        </div>
                                    </div>
                                )}

                                {(linkedProviders.has('NAVER') || naverEnabled || isOperator) && (
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <div className="text-sm font-medium">Naver</div>
                                            <div className="text-xs text-slate-500">
                                                {linkedProviders.has('NAVER') ? '연동됨' : '미연동'}
                                            </div>
                                        </div>
                                        <div className="min-w-[220px] flex justify-end">
                                            {linkedProviders.has('NAVER') ? (
                                                <button className="rounded border px-3 py-2 text-sm hover:bg-slate-50" type="button" onClick={() => unlinkProvider('naver')}>
                                                    연동 해제
                                                </button>
                                            ) : naverEnabled ? (
                                                <button
                                                    type="button"
                                                    className="rounded bg-[#03C75A] text-white px-3 py-2 text-sm font-semibold disabled:opacity-50"
                                                    onClick={async () => {
                                                        try {
                                                            const { code, state, redirectUri } = await naverLogin({})
                                                            await api.post('/users/me/social-links/naver', { code, state, redirectUri })
                                                            qc.invalidateQueries({ queryKey: ['me', 'socialLinks'] })
                                                            show('Naver 연동이 완료되었습니다', 'success')
                                                        } catch (e) {
                                                            const msg = e?.response?.data?.data?.message || e?.response?.data?.message || e?.message || 'Naver 연동에 실패했습니다'
                                                            show(msg, 'error')
                                                        }
                                                    }}
                                                >
                                                    네이버 연동
                                                </button>
                                            ) : (
                                                isOperator ? <div className="text-xs text-slate-500">설정 필요: `REACT_APP_NAVER_CLIENT_ID`</div> : null
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    </div>
                </div>

                <div className="flex min-w-0 flex-col gap-4">
                    <div className="flex items-center gap-2 px-1">
                        <span className="h-4 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
                        <div className="text-xs font-bold uppercase tracking-wide text-slate-500">구독 정보</div>
                    </div>
                    <div className="flex flex-1 flex-col gap-4">
                    {showBillingProfileSection ? (
                        <div className="rounded border p-4">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <h2 className="font-semibold text-slate-900">현재 구독 상태</h2>
                                {ENABLE_USER_BILLING ? (
                                    <Link className="rounded bg-brand-navy px-3 py-2 text-sm font-semibold text-white hover:opacity-90" to="/billing">
                                        구독관리 바로가기
                                    </Link>
                                ) : null}
                            </div>
                            {billingSummaryQuery.isLoading ? (
                                <div className="mt-4 text-sm text-slate-500">구독 정보를 불러오는 중입니다.</div>
                            ) : billingSummaryQuery.isError ? (
                                <div className="mt-4 text-sm text-slate-500">구독 정보를 불러오지 못했습니다.</div>
                            ) : (
                                <div className="mt-4 space-y-4">
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                        <ProfileBillingMetric label={orgContractPrimary ? '적용 플랜' : '구독 플랜'} value={profileSubscriptionPlanLabel} />
                                        <ProfileBillingMetric
                                            label={orgContractPrimary ? '적용 상태' : '잔여일'}
                                            value={orgContractPrimary ? '조직 결제 적용중' : formatProfileRemainingDays(profileSubscriptionEndsAt)}
                                        />
                                    </div>
                                    <div>
                                        <div className="mb-2 text-xs font-semibold text-slate-500">기능별 잔여 횟수</div>
                                        {profileFeatures.length > 0 ? (
                                            <div className="space-y-2">
                                                {profileFeatures.map((feature) => {
                                                    const counter = profileQuotaCounterMap.get(feature.featureCode)
                                                    return (
                                                        <div key={feature.featureCode || feature.featureName} className="flex items-center justify-between gap-3 rounded border bg-slate-50 px-3 py-2 text-sm">
                                                            <span className="font-medium text-slate-700">{formatProfileFeatureName(feature)}</span>
                                                            <span className="shrink-0 font-semibold text-slate-900">
                                                                {formatProfileFeatureRemaining(feature, counter)}
                                                            </span>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        ) : (
                                            <div className="rounded border border-dashed bg-slate-50 px-3 py-3 text-sm text-slate-500">
                                                표시할 잔여 횟수가 없습니다.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-1 flex-col rounded border border-dashed border-slate-200 bg-slate-50/80 p-4 text-sm text-slate-500">
                            구독·결제 정보는 이용 권한이 있는 계정에서 표시됩니다.
                        </div>
                    )}

                    {String(me.status || '').toUpperCase() === 'ACTIVE' && (
                        <div ref={accountDeletionSectionRef} className="rounded border border-slate-200 p-4">
                            <h2 className="font-semibold mb-2 text-slate-800">계정 탈퇴</h2>
                            <p className="text-xs text-slate-600 mb-3">
                                탈퇴 시 계정은 비활성화되고 로그인할 수 없으며, 이메일·휴대폰·소셜 연동은 재가입을 위해 해제됩니다.
                                조직 단위 이용 계약이 있는 경우 운영 승인이 필요합니다.
                            </p>
                            {withdrawEligibilityQuery.isLoading ? (
                                <div className="text-sm text-slate-500">조건 확인 중…</div>
                            ) : withdrawEligibilityQuery.isError ? (
                                <div className="text-sm text-slate-500">탈퇴 조건을 불러오지 못했습니다.</div>
                            ) : withdrawEligibilityQuery.data?.requiresApproval ? (
                                <button
                                    type="button"
                                    className="rounded border border-amber-300 text-amber-800 px-3 py-2 text-sm hover:bg-amber-50"
                                    onClick={() => { setWithdrawReqOpen(true); setWdPassword(''); setWdReqReason('') }}
                                >
                                    탈퇴 승인 요청
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="rounded border border-red-300 text-red-700 px-3 py-2 text-sm hover:bg-red-50"
                                    onClick={() => {
                                        setWithdrawOpen(true)
                                        setWdPassword('')
                                        setWdAckPrepaid(false)
                                        setWdAckIrrev(false)
                                    }}
                                >
                                    탈퇴하기
                                </button>
                            )}
                        </div>
                    )}
                    </div>
                </div>
            </div>

            <div className="pt-4 flex flex-wrap gap-2 items-center">
                <button
                    className="rounded bg-brand-navy text-white px-4 py-2 disabled:opacity-60"
                    disabled={updateMutation.isLoading || (SHOW_WORK_STATUS_AND_ORG_SECTION && (availability === 'VACATION' || availability === 'AWAY' || availability === 'TRAINING') && !deputyUserId1)}
                    onClick={() => updateMutation.mutate({
                        name,
                        jobTitle,
                        phoneNumber,
                        signatureFileId,
                        ...(SHOW_WORK_STATUS_AND_ORG_SECTION ? {
                            availabilityStatus: availability,
                            orgId: canChangeOrg && orgId ? Number(orgId) : undefined,
                            startAt: availStart ? new Date(availStart).toISOString() : undefined,
                            endAt: availEnd ? new Date(availEnd).toISOString() : undefined
                        } : {})
                    })}
                >
                    저장
                </button>
                {SHOW_WORK_STATUS_AND_ORG_SECTION && (availability === 'VACATION' || availability === 'AWAY' || availability === 'TRAINING') && !deputyUserId1 && (
                    <span className="text-xs text-red-600">교육/휴가/부재중 시 대직자 1순위를 선택하세요.</span>
                )}
                <button className="rounded border px-4 py-2" onClick={logout}>로그아웃</button>
            </div>

                {canTestNotification && (
                    <div className="mt-8 rounded border p-4 space-y-3">
                        <h2 className="font-semibold">웹알림 테스트 (운영자 전용)</h2>
                        <p className="text-xs text-slate-500">
                            아래 버튼을 누르면 현재 계정으로 SSE 웹알림 / FCM 푸시가 테스트용으로 전송됩니다.
                            알림 센터 UI와 브라우저 네트워크 탭의 <code className="px-1 py-0.5 bg-slate-100 rounded text-[10px]">/notifications/stream</code> 이벤트를 함께 확인해 보세요.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                className="rounded border px-3 py-2 text-sm disabled:opacity-60"
                                disabled={notifyLoading}
                                onClick={() => triggerNotificationTest('signup')}
                            >
                                가입 신청 알림 테스트
                            </button>
                            <button
                                type="button"
                                className="rounded border px-3 py-2 text-sm disabled:opacity-60"
                                disabled={notifyLoading}
                                onClick={() => triggerNotificationTest('request-created')}
                            >
                                의뢰 생성 알림 테스트
                            </button>
                            <button
                                type="button"
                                className="rounded border px-3 py-2 text-sm disabled:opacity-60"
                                disabled={notifyLoading}
                                onClick={() => triggerNotificationTest('request-assigned')}
                            >
                                의뢰 배정 알림 테스트
                            </button>
                        </div>
                        {notifyMessage && (
                            <div className="text-xs text-slate-600">{notifyMessage}</div>
                        )}
                    </div>
                )}

            {confirmOpen && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4">
                    <div className="w-full max-w-sm rounded bg-white p-6 space-y-4 text-sm">
                        <div className="text-base font-semibold">소속 변경 확인</div>
                        <div className="text-slate-700">소속을 변경하면 권한/접근 범위가 달라질 수 있습니다. 계속 진행하시겠습니까?</div>
                        <div className="text-xs text-slate-500">현재 소속: {originalOrgId || '-'} → 변경 소속: {nextOrgId || '-'}</div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button className="rounded border px-3 py-2" onClick={() => { setConfirmOpen(false); setNextOrgId('') }}>취소</button>
                            <button className="rounded bg-brand-navy text-white px-3 py-2" onClick={() => { setOrgId(nextOrgId); setConfirmOpen(false) }}>변경</button>
                        </div>
                    </div>
                </div>
            )}

            {withdrawOpen && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50">
                    <div className="w-full max-w-md rounded bg-white p-6 space-y-4 text-sm max-h-[90vh] overflow-y-auto">
                        <div className="text-base font-semibold text-red-800">계정 탈퇴</div>
                        <p className="text-xs text-slate-600">
                            탈퇴 후에는 같은 화면으로 로그인할 수 없습니다. 법령에 따라 일부 거래 기록은 보관될 수 있습니다.
                        </p>
                        {withdrawEligibilityQuery.data?.hasRemainingPrepaid && (
                            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                유효한 <strong>선결제 이용 기간</strong>이 남아 있습니다. 탈퇴 시 잔여 기간에 대한 환불은 제공하지 않을 수 있습니다(약관 및 법무 기준).
                                {withdrawEligibilityQuery.data?.prepaidEndsAt && (
                                    <span className="block mt-1 text-amber-800">이용 종료 예정: {formatApiDateTimeDisplay(withdrawEligibilityQuery.data.prepaidEndsAtDisplay, withdrawEligibilityQuery.data.prepaidEndsAt)}</span>
                                )}
                            </div>
                        )}
                        <label className="flex items-start gap-2 text-xs text-slate-700">
                            <input type="checkbox" checked={wdAckIrrev} onChange={(e) => setWdAckIrrev(e.target.checked)} className="mt-0.5" />
                            <span>위 내용을 확인했으며, 탈퇴를 진행합니다.</span>
                        </label>
                        {withdrawEligibilityQuery.data?.hasRemainingPrepaid && (
                            <label className="flex items-start gap-2 text-xs text-slate-700">
                                <input type="checkbox" checked={wdAckPrepaid} onChange={(e) => setWdAckPrepaid(e.target.checked)} className="mt-0.5" />
                                <span>잔여 선결제 이용 기간이 소멸되어도 이의가 없습니다.</span>
                            </label>
                        )}
                        <div>
                            <label className="block text-sm text-slate-600">비밀번호 확인</label>
                            <input type="password" className="mt-1 w-full rounded border px-3 py-2" value={wdPassword} onChange={(e) => setWdPassword(e.target.value)} autoComplete="current-password" />
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" className="rounded border px-3 py-2" onClick={() => setWithdrawOpen(false)}>취소</button>
                            <button
                                type="button"
                                className="rounded bg-red-600 text-white px-3 py-2 disabled:opacity-50"
                                disabled={
                                    withdrawMutation.isLoading
                                    || !wdPassword
                                    || !wdAckIrrev
                                    || (Boolean(withdrawEligibilityQuery.data?.hasRemainingPrepaid) && !wdAckPrepaid)
                                }
                                onClick={() => withdrawMutation.mutate()}
                            >
                                {withdrawMutation.isLoading ? '처리 중…' : '탈퇴 실행'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {withdrawReqOpen && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50">
                    <div className="w-full max-w-md rounded bg-white p-6 space-y-4 text-sm">
                        <div className="text-base font-semibold">탈퇴 승인 요청</div>
                        <p className="text-xs text-slate-600">
                            소속 조직에 유효한 조직 단위 이용 계약이 있어, 운영자 승인 후 탈퇴가 처리됩니다. 사유를 남겨 주세요.
                        </p>
                        <div>
                            <label className="block text-sm text-slate-600">탈퇴 사유</label>
                            <textarea className="mt-1 w-full rounded border px-3 py-2 min-h-[100px]" value={wdReqReason} onChange={(e) => setWdReqReason(e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-sm text-slate-600">비밀번호 확인</label>
                            <input type="password" className="mt-1 w-full rounded border px-3 py-2" value={wdPassword} onChange={(e) => setWdPassword(e.target.value)} autoComplete="current-password" />
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" className="rounded border px-3 py-2" onClick={() => setWithdrawReqOpen(false)}>취소</button>
                            <button
                                type="button"
                                className="rounded bg-brand-navy text-white px-3 py-2 disabled:opacity-50"
                                disabled={withdrawRequestMutation.isLoading || !wdPassword.trim() || !wdReqReason.trim()}
                                onClick={() => withdrawRequestMutation.mutate()}
                            >
                                {withdrawRequestMutation.isLoading ? '제출 중…' : '요청 제출'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {profileImagePreview && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${profileImagePreview.title} 크게 보기`}
                    onClick={() => setProfileImagePreview(null)}
                >
                    <div className="w-full max-w-3xl rounded-lg bg-white p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between gap-3">
                            <div className="text-sm font-semibold text-slate-900">{profileImagePreview.title}</div>
                            <button
                                type="button"
                                className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50"
                                onClick={() => setProfileImagePreview(null)}
                            >
                                닫기
                            </button>
                        </div>
                        <div className="mt-3 flex max-h-[78vh] items-center justify-center overflow-auto rounded bg-slate-50 p-3">
                            <img
                                src={profileImagePreview.src}
                                alt={profileImagePreview.title}
                                className="max-h-[74vh] max-w-full object-contain"
                            />
                        </div>
                    </div>
                </div>
            )}

            {pwOpen && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4">
                    <div className="w-full max-w-sm rounded bg-white p-6 space-y-4 text-sm">
                        <div className="text-base font-semibold">비밀번호 변경</div>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm text-slate-600">기존 비밀번호</label>
                                <input type="password" className="mt-1 w-full rounded border px-3 py-2" value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm text-slate-600">변경 비밀번호</label>
                                <input type="password" className="mt-1 w-full rounded border px-3 py-2" value={pwNext} onChange={(e) => setPwNext(e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm text-slate-600">변경 비밀번호 확인</label>
                                <input type="password" className="mt-1 w-full rounded border px-3 py-2" value={pwNextConfirm} onChange={(e) => setPwNextConfirm(e.target.value)} />
                                {pwNext && pwNextConfirm && pwNext !== pwNextConfirm && (
                                    <div className="text-xs text-red-600 mt-1">변경 비밀번호 확인이 일치하지 않습니다.</div>
                                )}
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button className="rounded border px-3 py-2" onClick={() => {
                                setPwOpen(false)
                                setPwCurrent('')
                                setPwNext('')
                                setPwNextConfirm('')
                            }}>취소</button>
                            <button
                                className="rounded bg-brand-navy text-white px-3 py-2 disabled:opacity-60"
                                disabled={changePasswordMutation.isLoading || !pwCurrent || !pwNext || !pwNextConfirm || pwNext !== pwNextConfirm || pwCurrent === pwNext}
                                onClick={() => changePasswordMutation.mutate({ currentPassword: pwCurrent, newPassword: pwNext, newPasswordConfirm: pwNextConfirm })}
                            >
                                {changePasswordMutation.isLoading ? '변경 중…' : '변경'}
                            </button>
                        </div>
                        {pwCurrent && pwNext && pwCurrent === pwNext && (
                            <div className="text-xs text-red-600">기존 비밀번호와 다른 비밀번호로 변경해 주세요.</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

function getProfileCutoutStatusMeta(status) {
    const s = String(status || '').toUpperCase()
    if (s === 'READY') {
        return { label: '생성 완료', className: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100' }
    }
    if (s === 'PROCESSING') {
        return { label: '생성 중', className: 'bg-sky-50 text-sky-700 ring-1 ring-sky-100' }
    }
    if (s === 'FAILED') {
        return { label: '다시 시도 필요', className: 'bg-rose-50 text-rose-700 ring-1 ring-rose-100' }
    }
    return { label: '미생성', className: 'bg-slate-100 text-slate-600 ring-1 ring-slate-200' }
}

function ProfileBillingMetric({ label, value }) {
    return (
        <div className="rounded border bg-white p-3">
            <div className="text-xs text-slate-500">{label}</div>
            <div className="mt-1 text-sm font-semibold text-slate-900">{value || '-'}</div>
        </div>
    )
}

function formatProfileRemainingDays(value) {
    if (!value) return '-'
    const end = new Date(value)
    if (Number.isNaN(end.getTime())) return '-'
    const diffMs = end.getTime() - Date.now()
    if (diffMs <= 0) return '만료'
    return `${Math.ceil(diffMs / (24 * 60 * 60 * 1000))}일`
}

function formatProfilePlanCodeLabel(value) {
    const normalized = String(value || '').toUpperCase()
    if (normalized === 'BASIC') return '베이직'
    if (normalized === 'STANDARD') return '스탠다드'
    if (normalized === 'PREMIUM') return '프리미엄'
    if (normalized === 'ENTERPRISE') return '엔터프라이즈'
    return ''
}

function formatProfileFeatureRemaining(feature, counter) {
    const quotaMode = String(counter?.quotaMode || feature?.quotaMode || '').toUpperCase()
    if (quotaMode === 'UNLIMITED') return '무제한'
    const unit = profileQuotaUnitLabel(feature?.quotaUnit || counter?.quotaUnit)
    const remaining = finiteNumberOrNull(counter?.remainingValue ?? counter?.remaining)
    const limit = finiteNumberOrNull(counter?.limitValue ?? counter?.monthlyLimit ?? feature?.monthlyLimit)
    const used = finiteNumberOrZero(counter?.usedValue ?? counter?.used)
    const reserved = finiteNumberOrZero(counter?.reservedValue ?? counter?.reserved)
    const resolvedRemaining = remaining ?? (limit == null ? null : Math.max(0, limit - used - reserved))
    if (resolvedRemaining == null) return '-'
    if (limit == null) return `${resolvedRemaining}${unit}`
    return `${resolvedRemaining}/${limit}${unit}`
}

function formatProfileFeatureName(feature) {
    const code = String(feature?.featureCode || '').toUpperCase()
    switch (code) {
        case 'CLAIM':
        case 'CLAIM_FAX':
        case 'CLAIM_INTAKE':
            return '청구'
        case 'HIDDEN_INSURANCE':
        case 'HIDDEN_INSURANCE_MONEY':
            return '숨은보험금'
        case 'HEALTH_ANALYSIS':
            return '진료내역분석'
        default:
            return feature?.featureName || '기능'
    }
}

function orderedProfileBillingFeatures(features) {
    const allowedOrder = ['CLAIM_FAX', 'CLAIM', 'CLAIM_INTAKE', 'HIDDEN_INSURANCE', 'HIDDEN_INSURANCE_MONEY', 'HEALTH_ANALYSIS']
    const orderByCode = new Map(allowedOrder.map((code, index) => [code, index]))
    return features
        .filter((feature) => orderByCode.has(String(feature?.featureCode || '').toUpperCase()))
        .sort((a, b) => {
            const ai = orderByCode.get(String(a?.featureCode || '').toUpperCase()) ?? 999
            const bi = orderByCode.get(String(b?.featureCode || '').toUpperCase()) ?? 999
            return ai - bi
        })
}

function finiteNumberOrZero(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function finiteNumberOrNull(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function profileQuotaUnitLabel(value) {
    switch (value) {
        case 'CASE':
        case 'COUNT':
            return '건'
        case 'RECIPIENT':
            return '명'
        case 'RUN':
            return '회'
        default:
            return ''
    }
}

function CalendarNotificationSettings() {
    const { show } = useToast()
    const qc = useQueryClient()
    const prefQuery = useQuery({
        queryKey: ['notification-preferences'],
        queryFn: async () => {
            const res = await api.get('/notification-preferences')
            return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        },
    })
    const [calPush, setCalPush] = useState(true)
    const [sel10, setSel10] = useState(false)
    const [sel60, setSel60] = useState(true)
    const [sel1440, setSel1440] = useState(false)

    useEffect(() => {
        const d = prefQuery.data
        if (!d) return
        setCalPush(d.calendarPushEnabled !== false)
        let arr = []
        try {
            if (d.calendarReminderOffsetsJson) {
                arr = JSON.parse(d.calendarReminderOffsetsJson)
            }
        } catch {
            arr = []
        }
        if (!Array.isArray(arr) || arr.length === 0) {
            setSel10(false)
            setSel60(true)
            setSel1440(false)
            return
        }
        setSel10(arr.includes(10))
        setSel60(arr.includes(60))
        setSel1440(arr.includes(1440))
    }, [prefQuery.data])

    const saveMut = useMutation({
        mutationFn: async () => {
            const mins = []
            if (sel10) mins.push(10)
            if (sel60) mins.push(60)
            if (sel1440) mins.push(1440)
            const body = {
                calendarPushEnabled: calPush,
                calendarReminderOffsetsJson: mins.length ? JSON.stringify(mins) : null,
            }
            const res = await api.patch('/notification-preferences', body)
            return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        },
        onSuccess: () => {
            show('일정 알림 설정을 저장했습니다', 'success')
            qc.invalidateQueries({ queryKey: ['notification-preferences'] })
        },
        onError: (e) => {
            const msg = e?.response?.data?.message || e?.message || '저장에 실패했습니다'
            show(msg, 'error')
        },
    })

    if (prefQuery.isLoading || prefQuery.isError) return null

    return (
        <div className="rounded border p-4 space-y-3">
            <h2 className="font-semibold text-slate-900">일정 알림</h2>
            <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={calPush} onChange={(e) => setCalPush(e.target.checked)} />
                일정 알림 받기
            </label>
            <div className="text-sm text-slate-600">시작 전 리마인더</div>
            <div className="flex flex-col gap-1 text-sm">
                <label className="flex items-center gap-2">
                    <input type="checkbox" checked={sel10} onChange={(e) => setSel10(e.target.checked)} />
                    10분 전
                </label>
                <label className="flex items-center gap-2">
                    <input type="checkbox" checked={sel60} onChange={(e) => setSel60(e.target.checked)} />
                    1시간 전
                </label>
                <label className="flex items-center gap-2">
                    <input type="checkbox" checked={sel1440} onChange={(e) => setSel1440(e.target.checked)} />
                    1일 전
                </label>
            </div>
            <button
                type="button"
                className="rounded bg-sky-600 text-white px-3 py-1.5 text-sm disabled:opacity-50"
                disabled={saveMut.isPending}
                onClick={() => saveMut.mutate()}
            >
                알림 설정 저장
            </button>
        </div>
    )
}

